import { formatDate } from "@core/helpers"
import { FEUILLES } from "@core/composables/useCompanyPlafond"

/**
 * Motif servi par source dans `GET /api/metier/company/billing/source`
 * (`MOTIFS_SERVIS` de l'API, `jugerSourceServie`, DEV-621 écran M2).
 */
export type MotifServi =
    | "active"
    | "supprimee_ajoutee_dans_la_periode"
    | "supprimee_apres_mise_a_jour"
    | "supprimee"

/** Jugement servi par l'API pour une source de la liste de facturation */
export type JugementServi = {
    /** Vrai : la source est facturée sur la période en cours */
    comptee?: boolean
    /** Fin de la période si la source est facturée, sinon null */
    comptee_jusqu_au?: string | null
    motif?: MotifServi | string
}

/** Cellule « Facturée sur la période » prête à afficher */
export type CelluleFacturation = {
    /** Feuille de l'arbre : « Oui », « Oui, jusqu'au JJ/MM » ou « Non, place libérée dès la suppression » */
    reponse: string
    /** Valeur du filtre oui / non */
    facturee: boolean
    /** Motif en clair, `null` si l'API ne l'a pas servi */
    motif: string | null
}

const MOTIFS: readonly string[] = [
    "active",
    "supprimee_ajoutee_dans_la_periode",
    "supprimee_apres_mise_a_jour",
    "supprimee",
]

/**
 * Indique si l'API a servi un motif lisible pour la source : le libellé suit
 * le motif (un `comptee` incohérent ne le contredit pas).
 */
export function aJugementServi(source: JugementServi): boolean {
    return typeof source.motif === "string" && MOTIFS.includes(source.motif)
}

/**
 * Chemin de la source dans l'arbre du décompte (décision du 14/10), en clair.
 * @returns le libellé, ou `null` pour un motif inconnu
 */
export function libelleMotif(motif: string | undefined): string | null {
    switch (motif) {
        case "active":
            return "Sur votre compte"
        case "supprimee_ajoutee_dans_la_periode":
            return "Supprimée pendant la période, ajoutée pendant la période"
        case "supprimee_apres_mise_a_jour":
            return "Supprimée pendant la période, ajoutée avant, mise à jour pendant la période"
        case "supprimee":
            return "Supprimée, ni ajoutée ni mise à jour pendant la période"
        default:
            return null
    }
}

/**
 * Feuille de l'arbre, mot pour mot, décidée par le motif servi :
 * - `active` : « Oui » ;
 * - `supprimee` : « Non, place libérée dès la suppression » ;
 * - supprimée encore facturée : « Oui, jusqu'au JJ/MM » avec
 *   `comptee_jusqu_au`, sinon la fin de période servie (`periodeFin`), sinon
 *   « Oui, jusqu'à la fin de la période ».
 * @param source - jugement servi
 * @param periodeFin - fin de la période servie par le bloc `plafond`, si connue
 */
export function feuilleFacturation(
    source: JugementServi,
    periodeFin?: string | null
): string {
    if (source.motif === "active") return FEUILLES.oui
    if (source.motif === "supprimee") return FEUILLES.non
    const fin =
        formatDate(source.comptee_jusqu_au, "DD/MM", "") ||
        formatDate(periodeFin, "DD/MM", "")
    return FEUILLES.jusqua(fin || null)
}

/**
 * Cellule « Facturée sur la période » lue du jugement servi.
 * @param source - jugement servi
 * @param periodeFin - fin de la période servie par le bloc `plafond`, si connue
 * @returns la cellule, ou `null` si l'API n'a pas servi de motif
 *   (le composant garde alors son calcul historique)
 */
export function celluleFacturation(
    source: JugementServi,
    periodeFin?: string | null
): CelluleFacturation | null {
    if (!aJugementServi(source)) return null
    return {
        reponse: feuilleFacturation(source, periodeFin),
        facturee: source.motif !== "supprimee",
        motif: libelleMotif(source.motif),
    }
}
